import React, { useState, useEffect } from 'react';
import { usePortvilleBilling } from '../../../../hooks/admin/usePortvilleBilling';
import { usePortvilleRooms } from '../../../../hooks/admin/usePortvilleRooms';
import { PortvilleUsageRecord, UsageRecordFormData, PortvilleBuildingUsageRecord, BuildingUsageRecordFormData, RoomBillingCalculation, PortvilleBillingSettings } from '../../../../types/portvilleBilling';
import UsageInputForm from '../../../../components/admin/portville/usage/UsageInputForm';
import UsageTable from '../../../../components/admin/portville/usage/UsageTable';
import BuildingUsageInputForm from '../../../../components/admin/portville/usage/BuildingUsageInputForm';
import BuildingUsageTable from '../../../../components/admin/portville/usage/BuildingUsageTable';
import RoomBillingTable from '../../../../components/admin/portville/usage/RoomBillingTable';
import ExcelUploadModal from '../../../../components/admin/portville/usage/ExcelUploadModal';
import { downloadUsageTemplate, exportUsageToExcel } from '../../../../utils/excelUtils';
import { portvilleBillingService } from '../../../../services/PortvilleBillingService';

const UsageManagePage: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState<number>(() => {
    const now = new Date();
    return now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  });
  const [selectedMonth, setSelectedMonth] = useState<number>(() => {
    const now = new Date();
    const lastMonth = now.getMonth() === 0 ? 12 : now.getMonth();
    return lastMonth;
  });
  const [activeTab, setActiveTab] = useState<'room' | 'building' | 'billing'>('room');
  const [isInputFormOpen, setIsInputFormOpen] = useState(false);
  const [isExcelUploadOpen, setIsExcelUploadOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<PortvilleUsageRecord | null>(null);
  const [editingBuildingRecord, setEditingBuildingRecord] = useState<PortvilleBuildingUsageRecord | null>(null);
  const [buildingUsageRecords, setBuildingUsageRecords] = useState<PortvilleBuildingUsageRecord[]>([]);
  const [buildingUsageLoading, setBuildingUsageLoading] = useState(false);
  const [roomBillingCalculations, setRoomBillingCalculations] = useState<RoomBillingCalculation[]>([]);
  const [billingCalculationLoading, setBillingCalculationLoading] = useState(false);

  // 늘해랑 관련 상태 추가
  const [neulhaerangBuildingUsageRecords, setNeulhaerangBuildingUsageRecords] = useState<PortvilleBuildingUsageRecord[]>([]);
  const [neulhaerangBuildingUsageLoading, setNeulhaerangBuildingUsageLoading] = useState(false);
  const [neulhaerangRoomBillingCalculations, setNeulhaerangRoomBillingCalculations] = useState<RoomBillingCalculation[]>([]);
  const [neulhaerangBillingCalculationLoading, setNeulhaerangBillingCalculationLoading] = useState(false);
  const [billingSettings, setBillingSettings] = useState<PortvilleBillingSettings[]>([]);

  const {
    usageRecords,
    buildingUsageRecords: hookBuildingUsageRecords,
    roomBillingCalculations: hookRoomBillingCalculations,
    savedBillingCalculations,
    loading: usageLoading,
    error: usageError,
    saveUsageRecords,
    deleteUsageRecord,
    saveBuildingUsageRecord,
    deleteBuildingUsageRecord,
    calculateRoomBilling,
    saveBillingCalculations,
    loadSavedBillingCalculations,
    deleteBillingCalculations,
    refreshData
  } = usePortvilleBilling(selectedYear, selectedMonth);

  const {
    rooms,
    loading: roomsLoading
  } = usePortvilleRooms();

  // 모든 호실과 시설물에서 사용량 입력 가능 (상태에 관계없이)
  const allRooms = rooms; // 모든 호실/시설물 포함

  // 관리비 설정을 기반으로 호실 타입 분류 함수
  const getRoomType = (roomId: string): '포트빌' | '늘해랑' => {
    const roomSettings = billingSettings.find(setting => setting.roomId === roomId);
    return roomSettings?.roomSettings?.roomType === '늘해랑' ? '늘해랑' : '포트빌'; // 기본값은 포트빌
  };

  // 호실 타입별 필터링 (관리비 설정 기반)
  const portvilleRooms = rooms.filter(room => getRoomType(room.id) === '포트빌');
  const neulhaerangRooms = rooms.filter(room => getRoomType(room.id) === '늘해랑');

  // 포트빌 사용량 기록 필터링
  const portvilleUsageRecords = usageRecords.filter(record => {
    return getRoomType(record.roomId) === '포트빌';
  });

  // 늘해랑 사용량 기록 필터링
  const neulhaerangUsageRecords = usageRecords.filter(record => {
    return getRoomType(record.roomId) === '늘해랑';
  });

  // 관리비 설정 로딩
  useEffect(() => {
    const loadBillingSettings = async () => {
      try {
        const allSettings = await portvilleBillingService.getAllBillingSettings();
        console.log('[DEBUG] 전체 관리비 설정 로딩:', allSettings.length, '개');

        // 각 호실별로 현재 유효한 설정만 선택 (isActive=true이거나 날짜 기준으로 유효한 설정)
        const now = new Date();
        const roomSettingsMap = new Map<string, PortvilleBillingSettings>();

        allSettings.forEach(setting => {
          const isCurrentlyValid = setting.isActive ||
            (setting.effectiveFrom <= now && (!setting.effectiveTo || setting.effectiveTo >= now));

          if (isCurrentlyValid) {
            const existingSetting = roomSettingsMap.get(setting.roomId);
            if (!existingSetting || setting.updatedAt > existingSetting.updatedAt) {
              roomSettingsMap.set(setting.roomId, setting);
            }
          }
        });

        const validSettings = Array.from(roomSettingsMap.values());
        setBillingSettings(validSettings);
        console.log('[DEBUG] 유효한 관리비 설정:', validSettings.length, '개');

      } catch (error) {
        console.error('관리비 설정 로딩 실패:', error);
      }
    };

    loadBillingSettings();
  }, []);

  // 연/월 변경 시 데이터 새로고침
  useEffect(() => {
    console.log(`[DEBUG] UsageManagePage - 연/월 변경됨: ${selectedYear}년 ${selectedMonth}월`);
    refreshData();
  }, [selectedYear, selectedMonth, refreshData]);

  // 훅의 데이터를 로컬 상태와 동기화
  useEffect(() => {
    setBuildingUsageRecords(hookBuildingUsageRecords);
  }, [hookBuildingUsageRecords]);

  useEffect(() => {
    setRoomBillingCalculations(hookRoomBillingCalculations);
  }, [hookRoomBillingCalculations]);

  // 저장된 계산 결과 로드
  useEffect(() => {
    if (activeTab === 'billing') {
      loadSavedBillingCalculations(selectedYear, selectedMonth);
    }
  }, [activeTab, selectedYear, selectedMonth, loadSavedBillingCalculations]);

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
  };

  const handleMonthChange = (month: number) => {
    setSelectedMonth(month);
  };

  const handleOpenInputForm = (record?: PortvilleUsageRecord) => {
    setEditingRecord(record || null);
    setIsInputFormOpen(true);
  };

  const handleCloseInputForm = () => {
    setIsInputFormOpen(false);
    setEditingRecord(null);
  };

  const handleUsageSaved = () => {
    handleCloseInputForm();
    refreshData();
  };

  const handleEditRecord = (record: PortvilleUsageRecord) => {
    handleOpenInputForm(record);
  };

  const handleDeleteRecord = async (recordId: string) => {
    if (window.confirm('정말로 이 사용량 기록을 삭제하시겠습니까?')) {
      try {
        await deleteUsageRecord(recordId);
        refreshData();
      } catch (error) {
        console.error('사용량 기록 삭제 실패:', error);
        alert('사용량 기록 삭제에 실패했습니다.');
      }
    }
  };

  // 엑셀 템플릿 다운로드
  const handleDownloadTemplate = () => {
    try {
      downloadUsageTemplate(allRooms, selectedYear, selectedMonth);
    } catch (error) {
      console.error('템플릿 다운로드 실패:', error);
      alert('템플릿 다운로드에 실패했습니다.');
    }
  };

  // 엑셀 데이터 내보내기
  const handleExportToExcel = () => {
    try {
      exportUsageToExcel(usageRecords, allRooms, selectedYear, selectedMonth);
    } catch (error) {
      console.error('엑셀 내보내기 실패:', error);
      alert('엑셀 내보내기에 실패했습니다.');
    }
  };

  // 엑셀 업로드
  const handleExcelUpload = async (data: UsageRecordFormData[]) => {
    try {
      const success = await saveUsageRecords(data, 'admin'); // TODO: 실제 사용자 ID로 변경
      if (success) {
        refreshData();
        alert(`${data.length}개의 사용량 데이터가 성공적으로 저장되었습니다.`);
      } else {
        alert('사용량 데이터 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('엑셀 업로드 실패:', error);
      alert('엑셀 업로드에 실패했습니다.');
    }
  };

  // 포트빌 건물 전체 사용량 조회
  const loadBuildingUsageRecords = async () => {
    setBuildingUsageLoading(true);
    try {
      const records = await portvilleBillingService.getBuildingUsageRecords(selectedYear, selectedMonth);
      setBuildingUsageRecords(records);
    } catch (error) {
      console.error('포트빌 건물 전체 사용량 조회 실패:', error);
      alert('포트빌 건물 전체 사용량 조회에 실패했습니다.');
    } finally {
      setBuildingUsageLoading(false);
    }
  };

  // 통합된 건물 전체 사용량이므로 별도의 늘해랑 로딩 함수는 불필요
  // loadBuildingUsageRecords에서 포트빌과 늘해랑 데이터가 모두 포함된 통합 레코드를 로딩함

  // 건물 전체 사용량 저장
  const handleBuildingUsageSave = async (data: BuildingUsageRecordFormData) => {
    try {
      if (editingBuildingRecord) {
        await portvilleBillingService.updateBuildingUsageRecord(editingBuildingRecord.id, data, 'admin');
        alert('건물 전체 사용량이 수정되었습니다.');
      } else {
        await portvilleBillingService.saveBuildingUsageRecord(data, 'admin');
        alert('건물 전체 사용량이 저장되었습니다.');
      }
      setEditingBuildingRecord(null);
      setIsInputFormOpen(false);
      loadBuildingUsageRecords();
    } catch (error) {
      console.error('건물 전체 사용량 저장 실패:', error);
      alert('건물 전체 사용량 저장에 실패했습니다.');
    }
  };

  // 건물 전체 사용량 수정
  const handleBuildingUsageEdit = (record: PortvilleBuildingUsageRecord) => {
    setEditingBuildingRecord(record);
    setIsInputFormOpen(true);
  };

  // 건물 전체 사용량 삭제
  const handleBuildingUsageDelete = async (recordId: string) => {
    if (window.confirm('정말로 이 건물 전체 사용량 기록을 삭제하시겠습니까?')) {
      try {
        await portvilleBillingService.deleteBuildingUsageRecord(recordId);
        alert('건물 전체 사용량이 삭제되었습니다.');
        loadBuildingUsageRecords();
      } catch (error) {
        console.error('건물 전체 사용량 삭제 실패:', error);
        alert('건물 전체 사용량 삭제에 실패했습니다.');
      }
    }
  };

  // 건물 전체 사용량 폼 취소
  const handleBuildingUsageCancel = () => {
    setEditingBuildingRecord(null);
    setIsInputFormOpen(false);
  };

  // 건물 전체 사용량 추가
  const handleBuildingUsageAdd = () => {
    setEditingBuildingRecord(null);
    setIsInputFormOpen(true);
  };

  // 템플릿 다운로드 이벤트 리스너
  useEffect(() => {
    const handleDownloadTemplateEvent = () => {
      handleDownloadTemplate();
    };

    window.addEventListener('downloadTemplate', handleDownloadTemplateEvent);
    return () => {
      window.removeEventListener('downloadTemplate', handleDownloadTemplateEvent);
    };
  }, [allRooms, selectedYear, selectedMonth]);

  // 건물 전체 사용량 조회
  useEffect(() => {
    if (activeTab === 'building') {
      loadBuildingUsageRecords();
      // 통합 레코드이므로 별도의 늘해랑 로딩 불필요
    }
  }, [activeTab, selectedYear, selectedMonth]);

  // 포트빌 호실별 요금 계산
  const handleCalculateRoomBilling = async () => {
    try {
      setBillingCalculationLoading(true);
      await calculateRoomBilling(selectedYear, selectedMonth);
    } catch (error) {
      console.error('포트빌 호실별 요금 계산 실패:', error);
    } finally {
      setBillingCalculationLoading(false);
    }
  };

  // 늘해랑 호실별 요금 계산
  const handleCalculateNeulhaerangRoomBilling = async () => {
    try {
      setNeulhaerangBillingCalculationLoading(true);
      // 늘해랑 타입 호실별 요금 계산을 위한 별도 로직
      // 기존 함수를 사용하되 결과를 늘해랑용으로 필터링
      await calculateRoomBilling(selectedYear, selectedMonth);
      // hookRoomBillingCalculations에서 늘해랑 호실만 필터링하여 설정
      const neulhaerangCalculations = hookRoomBillingCalculations.filter(calc => {
        return getRoomType(calc.roomId) === '늘해랑';
      });
      setNeulhaerangRoomBillingCalculations(neulhaerangCalculations);
    } catch (error) {
      console.error('늘해랑 호실별 요금 계산 실패:', error);
    } finally {
      setNeulhaerangBillingCalculationLoading(false);
    }
  };

  // 계산 결과 저장
  const handleSaveBillingCalculations = async () => {
    try {
      if (roomBillingCalculations.length === 0) {
        alert('저장할 계산 결과가 없습니다. 먼저 요금을 계산해주세요.');
        return;
      }

      if (buildingUsageRecords.length === 0) {
        alert('건물 전체 사용량 기록이 없습니다.');
        return;
      }

      const buildingUsageRecordId = buildingUsageRecords[0].id;
      const success = await saveBillingCalculations(selectedYear, selectedMonth, buildingUsageRecordId);
      
      if (success) {
        alert('계산 결과가 저장되었습니다.');
      } else {
        alert('계산 결과 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('계산 결과 저장 실패:', error);
      alert('계산 결과 저장에 실패했습니다.');
    }
  };


  // 저장된 계산 결과 삭제
  const handleDeleteBillingCalculations = async () => {
    try {
      if (savedBillingCalculations.length === 0) {
        alert('삭제할 계산 결과가 없습니다.');
        return;
      }

      const confirmed = window.confirm(
        `저장된 계산 결과를 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`
      );

      if (!confirmed) return;

      await deleteBillingCalculations(selectedYear, selectedMonth);
      alert('계산 결과가 삭제되었습니다.');
    } catch (error) {
      console.error('계산 결과 삭제 실패:', error);
      alert('계산 결과 삭제에 실패했습니다.');
    }
  };

  // 포트빌 사용량 통계 계산
  const portvilleTotalElectricityIndividualUsage = portvilleUsageRecords.reduce((sum, record) => sum + (record.electricityIndividualUsage || 0), 0);
  const portvilleTotalElectricityCommonUsage = portvilleUsageRecords.reduce((sum, record) => sum + (record.electricityCommonUsage || 0), 0);
  const portvilleTotalWaterIndividualUsage = portvilleUsageRecords.reduce((sum, record) => sum + (record.waterIndividualUsage || 0), 0);
  const portvilleTotalWaterCommonUsage = portvilleUsageRecords.reduce((sum, record) => sum + (record.waterCommonUsage || 0), 0);
  const portvilleRecordsCount = portvilleUsageRecords.length;
  const portvilleRoomsCount = portvilleRooms.length;

  // 늘해랑 사용량 통계 계산
  const neulhaerangTotalElectricityIndividualUsage = neulhaerangUsageRecords.reduce((sum, record) => sum + (record.electricityIndividualUsage || 0), 0);
  const neulhaerangTotalElectricityCommonUsage = neulhaerangUsageRecords.reduce((sum, record) => sum + (record.electricityCommonUsage || 0), 0);
  const neulhaerangTotalWaterIndividualUsage = neulhaerangUsageRecords.reduce((sum, record) => sum + (record.waterIndividualUsage || 0), 0);
  const neulhaerangTotalWaterCommonUsage = neulhaerangUsageRecords.reduce((sum, record) => sum + (record.waterCommonUsage || 0), 0);
  const neulhaerangRecordsCount = neulhaerangUsageRecords.length;
  const neulhaerangRoomsCount = neulhaerangRooms.length;

  // 전체 통계 (기존 코드와의 호환성 유지)
  const totalElectricityIndividualUsage = usageRecords.reduce((sum, record) => sum + (record.electricityIndividualUsage || 0), 0);
  const totalElectricityCommonUsage = usageRecords.reduce((sum, record) => sum + (record.electricityCommonUsage || 0), 0);
  const totalWaterIndividualUsage = usageRecords.reduce((sum, record) => sum + (record.waterIndividualUsage || 0), 0);
  const totalWaterCommonUsage = usageRecords.reduce((sum, record) => sum + (record.waterCommonUsage || 0), 0);
  const recordsCount = usageRecords.length;
  const allRoomsCount = allRooms.length;

  if (roomsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">호실 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* 페이지 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">사용량 관리</h1>
        <p className="text-gray-600 mt-1">월별 전기 및 수도 사용량을 입력하고 관리합니다</p>
      </div>

      {/* 탭 네비게이션 */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('room')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'room'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              호실별 사용량
            </button>
            <button
              onClick={() => setActiveTab('building')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'building'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              건물 전체 사용량
            </button>
            <button
              onClick={() => setActiveTab('billing')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'billing'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              호실별 요금 계산
            </button>
          </nav>
        </div>
      </div>

      {/* 탭 내용 */}
      {activeTab === 'room' && (
        <>
          {/* 포트빌 통계 카드 */}
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">포트빌 사용량 현황</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">총 전기 사용량</p>
              <p className="text-2xl font-bold text-gray-900">
                {(totalElectricityIndividualUsage + totalElectricityCommonUsage).toLocaleString()} kWh
              </p>
              <p className="text-xs text-gray-500">
                개별: {totalElectricityIndividualUsage.toLocaleString()} kWh, 
                공용: {totalElectricityCommonUsage.toLocaleString()} kWh
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">포트빌 총 수도 사용량</p>
              <p className="text-2xl font-bold text-gray-900">
                {(portvilleTotalWaterIndividualUsage + portvilleTotalWaterCommonUsage).toLocaleString()} 톤
              </p>
              <p className="text-xs text-gray-500">
                개별: {portvilleTotalWaterIndividualUsage.toLocaleString()} 톤,
                공용: {portvilleTotalWaterCommonUsage.toLocaleString()} 톤
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">포트빌 입력된 호실</p>
              <p className="text-2xl font-bold text-gray-900">{portvilleRecordsCount} / {portvilleRoomsCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">포트빌 입력률</p>
              <p className="text-2xl font-bold text-gray-900">
                {portvilleRoomsCount > 0 ? Math.round((portvilleRecordsCount / portvilleRoomsCount) * 100) : 0}%
              </p>
            </div>
          </div>
        </div>
      </div>
          </div>

          {/* 늘해랑 통계 카드 */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">늘해랑 사용량 현황</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* 늘해랑 총 수도 사용량 */}
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">늘해랑 총 수도 사용량</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {(neulhaerangTotalWaterIndividualUsage + neulhaerangTotalWaterCommonUsage).toLocaleString()} 톤
                    </p>
                    <p className="text-xs text-gray-500">
                      개별: {neulhaerangTotalWaterIndividualUsage.toLocaleString()} 톤,
                      공용: {neulhaerangTotalWaterCommonUsage.toLocaleString()} 톤
                    </p>
                  </div>
                </div>
              </div>

              {/* 늘해랑 입력된 호실 */}
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">늘해랑 입력된 호실</p>
                    <p className="text-2xl font-bold text-gray-900">{neulhaerangRecordsCount} / {neulhaerangRoomsCount}</p>
                  </div>
                </div>
              </div>

              {/* 늘해랑 입력률 */}
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">늘해랑 입력률</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {neulhaerangRoomsCount > 0 ? Math.round((neulhaerangRecordsCount / neulhaerangRoomsCount) * 100) : 0}%
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

      {/* 필터 및 액션 바 */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* 연/월 선택 */}
          <div className="flex items-center gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">연도</label>
              <select
                value={selectedYear}
                onChange={(e) => handleYearChange(Number(e.target.value))}
                className="block w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - 2 + i;
                  return (
                    <option key={year} value={year}>
                      {year}년
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">월</label>
              <select
                value={selectedMonth}
                onChange={(e) => handleMonthChange(Number(e.target.value))}
                className="block w-24 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}월
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 액션 버튼 */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => handleOpenInputForm()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              사용량 입력
            </button>
            <button
              onClick={() => setIsExcelUploadOpen(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              엑셀 업로드
            </button>
            <button
              onClick={handleDownloadTemplate}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              템플릿 다운로드
            </button>
            {usageRecords.length > 0 && (
              <button
                onClick={handleExportToExcel}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
              >
                엑셀 내보내기
              </button>
            )}
            <button
              onClick={refreshData}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              새로고침
            </button>
          </div>
        </div>
      </div>

      {/* 사용량 테이블 */}
      <div className="bg-white rounded-lg shadow">
        <UsageTable
          usageRecords={usageRecords}
          activeRooms={allRooms}
          loading={usageLoading}
          error={usageError}
          onEdit={handleEditRecord}
          onDelete={handleDeleteRecord}
        />
      </div>

      {/* 사용량 입력 모달 */}
      {isInputFormOpen && (
        <UsageInputForm
          isOpen={isInputFormOpen}
          onClose={handleCloseInputForm}
          onSave={handleUsageSaved}
          editingRecord={editingRecord}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          activeRooms={allRooms}
        />
      )}

          {/* 엑셀 업로드 모달 */}
          {isExcelUploadOpen && (
            <ExcelUploadModal
              isOpen={isExcelUploadOpen}
              onClose={() => setIsExcelUploadOpen(false)}
              onUpload={handleExcelUpload}
              activeRooms={allRooms}
              selectedYear={selectedYear}
              selectedMonth={selectedMonth}
            />
          )}
        </>
      )}

      {/* 건물 전체 사용량 탭 */}
      {activeTab === 'building' && (
        <>
          {/* 건물 전체 사용량 액션 바 */}
          <div className="bg-white p-6 rounded-lg shadow mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {/* 연/월 선택 */}
              <div className="flex items-center gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">연도</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    className="block w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Array.from({ length: 5 }, (_, i) => {
                      const year = new Date().getFullYear() - 2 + i;
                      return (
                        <option key={year} value={year}>
                          {year}년
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">월</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    className="block w-24 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <option key={month} value={month}>
                        {month}월
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 액션 버튼들 */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setEditingBuildingRecord(null);
                    setIsInputFormOpen(true);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  건물 사용량 추가
                </button>
                <button
                  onClick={() => {
                    loadBuildingUsageRecords();
                    // 통합 레코드이므로 별도의 늘해랑 로딩 불필요
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  새로고침
                </button>
              </div>
            </div>
          </div>

          {/* 통합 건물 전체 사용량 테이블 */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold text-gray-800 mb-3">건물 전체 사용량 (포트빌/늘해랑)</h4>

            {/* 통합 테이블 - 포트빌과 늘해랑 데이터를 합쳐서 표시 */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">연도/월</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">전기요금</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">전기 개별/공용</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">포트빌 수도요금</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">포트빌 수도 개별/공용</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">늘해랑 수도요금</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">늘해랑 수도 개별/공용</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">작업</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {buildingUsageLoading ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                            <span className="ml-2">로딩 중...</span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      // 통합된 건물 사용량 레코드 표시
                      (() => {
                        // 모든 건물 사용량 레코드를 연도/월별로 그룹화 (이제 단일 레코드에 포트빌/늘해랑 데이터 모두 포함)
                        const allRecords = [...buildingUsageRecords];
                        const sortedRecords = allRecords.sort((a, b) => {
                          if (a.period.year !== b.period.year) return b.period.year - a.period.year;
                          return b.period.month - a.period.month;
                        });

                        return sortedRecords.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                              등록된 건물 사용량이 없습니다.
                            </td>
                          </tr>
                        ) : sortedRecords.map(record => {
                          return (
                            <tr key={`${record.period.year}-${record.period.month}`} className="hover:bg-gray-50">
                              <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {record.period.year}년 {record.period.month}월
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                {record.totalElectricityFee ? `${record.totalElectricityFee.toLocaleString()}원` : '-'}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                {/* 전기 개별/공용 사용량 */}
                                <div className="text-xs">
                                  <div>개별: {record.buildingIndividualUsage ? `${record.buildingIndividualUsage.toLocaleString()}kWh` : '-'}</div>
                                  <div>공용: {record.buildingCommonUsage ? `${record.buildingCommonUsage.toLocaleString()}kWh` : '-'}</div>
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                {/* 포트빌 수도요금 (새 필드가 있으면 사용, 없으면 기존 필드 사용) */}
                                {(() => {
                                  const portvilleFee = (record as any).portvilleTotalWaterFee ?? record.totalWaterFee;
                                  return (portvilleFee && typeof portvilleFee === 'number') ? `${portvilleFee.toLocaleString()}원` : '-';
                                })()}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                {/* 포트빌 수도 개별/공용 사용량 */}
                                <div className="text-xs">
                                  <div>개별: {(() => {
                                    const portvilleIndividual = (record as any).portvilleBuildingWaterIndividualUsage ?? record.buildingWaterIndividualUsage;
                                    return (portvilleIndividual && typeof portvilleIndividual === 'number') ? `${portvilleIndividual.toLocaleString()}톤` : '-';
                                  })()}</div>
                                  <div>공용: {(() => {
                                    const portvilleCommon = (record as any).portvilleBuildingWaterCommonUsage ?? record.buildingWaterCommonUsage;
                                    return (portvilleCommon && typeof portvilleCommon === 'number') ? `${portvilleCommon.toLocaleString()}톤` : '-';
                                  })()}</div>
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                {/* 늘해랑 수도요금 */}
                                {(() => {
                                  const neulhaerangFee = (record as any).neulhaerangTotalWaterFee;
                                  return (neulhaerangFee && typeof neulhaerangFee === 'number') ? `${neulhaerangFee.toLocaleString()}원` : '-';
                                })()}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                {/* 늘해랑 수도 개별/공용 사용량 */}
                                <div className="text-xs">
                                  <div>개별: {(() => {
                                    const neulhaerangIndividual = (record as any).neulhaerangBuildingWaterIndividualUsage;
                                    return (neulhaerangIndividual && typeof neulhaerangIndividual === 'number') ? `${neulhaerangIndividual.toLocaleString()}톤` : '-';
                                  })()}</div>
                                  <div>공용: {(() => {
                                    const neulhaerangCommon = (record as any).neulhaerangBuildingWaterCommonUsage;
                                    return (neulhaerangCommon && typeof neulhaerangCommon === 'number') ? `${neulhaerangCommon.toLocaleString()}톤` : '-';
                                  })()}</div>
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                                <button
                                  onClick={() => {
                                    setEditingBuildingRecord(record);
                                    setIsInputFormOpen(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-900 mr-4"
                                >
                                  수정
                                </button>
                                <button
                                  onClick={async () => {
                                    if (window.confirm(`정말로 ${record.period.year}년 ${record.period.month}월 건물 사용량 기록을 삭제하시겠습니까?`)) {
                                      try {
                                        await portvilleBillingService.deleteBuildingUsageRecord(record.id);
                                        alert('건물 사용량이 삭제되었습니다.');
                                        loadBuildingUsageRecords();
                                        // 통합 레코드이므로 별도의 늘해랑 로딩 불필요
                                      } catch (error) {
                                        console.error('건물 사용량 삭제 실패:', error);
                                        alert('건물 사용량 삭제에 실패했습니다.');
                                      }
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  삭제
                                </button>
                              </td>
                            </tr>
                          );
                        });
                      })()
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* 건물 전체 사용량 입력 모달 */}
          {isInputFormOpen && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
                <BuildingUsageInputForm
                  selectedRecord={editingBuildingRecord}
                  onSave={async (data) => {
                    try {
                      // 통합된 데이터로 저장 (포트빌과 늘해랑 데이터 모두 포함)
                      const unifiedData = {
                        ...data,
                        // 이전 호환성을 위해 기존 필드도 설정 (기본적으로 포트빌 데이터로)
                        totalWaterFee: data.portvilleTotalWaterFee,
                        buildingWaterIndividualUsage: data.portvilleBuildingWaterIndividualUsage,
                        buildingWaterCommonUsage: data.portvilleBuildingWaterCommonUsage
                      } as any;

                      if (editingBuildingRecord && editingBuildingRecord.id) {
                        // 기존 레코드 수정
                        await portvilleBillingService.updateBuildingUsageRecord(editingBuildingRecord.id, unifiedData, 'admin');
                        alert('건물 전체 사용량이 수정되었습니다.');
                      } else {
                        // 새 레코드 생성
                        await portvilleBillingService.saveBuildingUsageRecord(unifiedData, 'admin');
                        alert('건물 전체 사용량이 저장되었습니다.');
                      }
                      setEditingBuildingRecord(null);
                      setIsInputFormOpen(false);
                      loadBuildingUsageRecords();
                      // 통합 레코드이므로 별도의 늘해랑 로딩 불필요
                    } catch (error) {
                      console.error('건물 전체 사용량 저장 실패:', error);
                      alert('건물 전체 사용량 저장에 실패했습니다.');
                    }
                  }}
                  onCancel={() => {
                    setEditingBuildingRecord(null);
                    setIsInputFormOpen(false);
                  }}
                  loading={false}
                  selectedYear={selectedYear}
                  selectedMonth={selectedMonth}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* 호실별 요금 계산 탭 */}
      {activeTab === 'billing' && (
        <>
          {/* 포트빌 요금 계산 컨트롤 */}
          <div className="mb-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">호실별 요금 계산</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedYear}년 {selectedMonth}월 전체 호실별 전기 및 수도 요금을 계산합니다 (포트빌/늘해랑 통합)
                  </p>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={async () => {
                      try {
                        setBillingCalculationLoading(true);
                        setNeulhaerangBillingCalculationLoading(true);

                        // 전체 호실 사용량 데이터로 통합 계산
                        await calculateRoomBilling(selectedYear, selectedMonth);

                        alert('전체 호실별 요금 계산이 완료되었습니다.');
                      } catch (error) {
                        console.error('통합 요금 계산 실패:', error);
                        alert('요금 계산에 실패했습니다.');
                      } finally {
                        setBillingCalculationLoading(false);
                        setNeulhaerangBillingCalculationLoading(false);
                      }
                    }}
                    disabled={billingCalculationLoading || usageRecords.length === 0 || buildingUsageRecords.length === 0}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {billingCalculationLoading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        계산 중...
                      </div>
                    ) : (
                      '통합 요금 계산'
                    )}
                  </button>

                  <button
                    onClick={handleSaveBillingCalculations}
                    disabled={roomBillingCalculations.length === 0 || buildingUsageRecords.length === 0}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    계산 결과 저장
                  </button>

                  <button
                    onClick={handleDeleteBillingCalculations}
                    disabled={savedBillingCalculations.length === 0}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    결과 삭제
                  </button>
                </div>
              </div>

              {/* 통합 계산 조건 확인 */}
              <div className="mt-4 p-4 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-700 mb-2">계산 조건</h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                  <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-2 ${portvilleUsageRecords.length > 0 ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span>포트빌 호실: {portvilleUsageRecords.length}개</span>
                  </div>
                  <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-2 ${neulhaerangUsageRecords.length > 0 ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span>늘해랑 호실: {neulhaerangUsageRecords.length}개</span>
                  </div>
                  <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-2 ${buildingUsageRecords.length > 0 ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span>건물 사용량: {buildingUsageRecords.length}개</span>
                  </div>
                  <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-2 ${savedBillingCalculations.length > 0 ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <span>저장된 결과: {savedBillingCalculations.length}개</span>
                  </div>
                </div>
                {usageRecords.length === 0 && (
                  <p className="text-red-600 text-sm mt-2">호실별 사용량을 먼저 입력해주세요.</p>
                )}
                {buildingUsageRecords.length === 0 && (
                  <p className="text-red-600 text-sm mt-2">건물 전체 사용량을 먼저 입력해주세요.</p>
                )}
              </div>
            </div>
          </div>

          {/* 통합 호실별 요금 계산 결과 테이블 */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold text-gray-800 mb-3">호실별 요금 계산 결과 (통합)</h4>
            <RoomBillingTable
              calculations={roomBillingCalculations}
              loading={billingCalculationLoading}
            />
          </div>

        </>
      )}
    </div>
  );
};

export default UsageManagePage;
